/**
 * Routes where the global admin chrome must NOT render.
 *
 * The HFv3 app stuffs three chrome components into the root layout —
 * `<HeaderControls>` (globe / Ask AI / Theme), `<ChatCompanion>` (the
 * floating AI panel), and `<FeedbackWidget>` (the bottom-right chat
 * bubble). All three are admin-context affordances that customers
 * (employees in the portal, admins impersonating an employee in the
 * preview routes, anonymous visitors on /login, etc.) must NEVER see.
 *
 * Before this module existed, each of the three components carried its
 * own private copy of the prefix list. They drifted (the audit on
 * 2026-05-12 found `/portal-preview` missing from one, `/teams-portal`
 * missing from all three). Now they all import this single constant —
 * any future addition lands in one place.
 *
 * The matching rule is exact-equals OR prefix-with-slash. So `/portal`
 * matches `/portal` and `/portal/login` but NOT `/portal-settings` or
 * `/portal-preview` (those need to be added explicitly if hidden).
 */
export const HIDDEN_ROUTE_PREFIXES: readonly string[] = [
  // Pre-auth + tenant-less surfaces
  '/login',
  '/auth',
  '/onboarding',
  '/admin',

  // Full-screen setup flows (the Gmail add-on's): they have no header to hold this chrome, draw their
  // own language switcher, and a sticky footer the feedback bubble would sit on.
  '/setup',

  '/access-denied',
  '/verify',
  '/accept-invite',

  // Live employee portal (clean, customer-facing).
  '/portal',

  // Admin "view-as" / impersonation previews — visually IDENTICAL to the
  // real portal so the admin can see what the employee will see. URL
  // segment `(portal-preview)` is a Next.js route GROUP and does NOT
  // appear in the path; the real paths are listed below.
  '/portal-preview',
  '/portal-theme-preview',
  '/teams-portal',
  '/user-portal',

  // SCORM / training player — full-bleed, no chrome.
  '/player',

  // The PhishTrainer play portal and public certificate verification: learner
  // and visitor surfaces with their own language switcher. Without these, a
  // visitor also signed in to the admin app got a second switcher (and the
  // admin's Ask AI / feedback chrome) on top of them (B02 review, 2026-09-26).
  // Note `/play` is not `/phishtrainer/play`: whole segments only.
  '/play',
  '/certificates/verify',

  // The outside auditor's read-only Policies area (Policies v3): an auditor is
  // not a workspace user, so an admin signed in in the same browser must not
  // get Ask AI, the feedback bubble or the header controls drawn over it.
  // Guarded by lib/policies/__tests__/audit-public-area.guard.spec.ts.
  '/policy-audit',
] as const;

/**
 * True when `pathname` falls under any hidden prefix.
 *
 * Matching is exact OR prefix-with-slash (so `/portal` matches `/portal`
 * and `/portal/login` but not `/portal-settings`).
 */
/**
 * Previews that live INSIDE /settings (so a prefix cannot name them without
 * hiding their sibling settings pages): the View as Manager team view,
 * /settings/portal/manager/view-as/<managerId>. It draws its own preview
 * banner across the top, and the admin's fixed header controls sat under it
 * ("Exit preview" over "Ask AI" and "Theme"). The picker list
 * (/settings/portal/manager/view-as) is an ordinary settings page.
 */
export const HIDDEN_ROUTE_PATTERNS: readonly RegExp[] = [
  /^\/settings\/portal\/manager\/view-as\/[^/]+\/?$/,
] as const;

export function isHiddenChromeRoute(pathname: string): boolean {
  return (
    HIDDEN_ROUTE_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    ) || HIDDEN_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))
  );
}
